import { useEffect, useRef, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Stack, Tooltip, Typography,
} from '@mui/material'
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import { useOrgSettings } from '../hooks/useOrgSettings'

/**
 * WhatsApp-style image markup. Drop a `File` in, get an annotated
 * `File` out (same filename + dimensions; PNG-encoded so the strokes
 * are crisp). The component opens as a modal — the caller decides
 * when to mount it (typically after the file picker fires and before
 * the upload starts).
 *
 * Design notes:
 *  - Strokes are stored at original-image resolution, NOT at the
 *    rendered canvas size. That way `Undo` / `Clear` don't lose
 *    fidelity as the user resizes the window mid-edit, and the
 *    exported PNG is full-resolution.
 *  - Save flattens the strokes onto a hidden offscreen canvas at the
 *    image's native size, encodes to PNG, and wraps it in a `File`
 *    that mirrors the source file's name. The caller's upload code
 *    doesn't need to know annotation happened.
 *  - Skip button returns the original file untouched — useful when
 *    the user opened the editor by accident or the photo is already
 *    fine as-is.
 */

interface Stroke {
  color: string
  width: number   // image-space px
  points: { x: number; y: number }[]
}

const COLORS = ['#EF4444', '#F59E0B', '#10B981', '#0EA5E9', '#A855F7', '#111827', '#FFFFFF']
const DEFAULT_COLOR = '#EF4444'
const STROKE_WIDTH = 6  // image-space px — visually similar across most screen sizes

interface Props {
  open: boolean
  /** The user's picked image. Annotator only opens for `image/*`; the
   *  caller is responsible for short-circuiting other MIME types. */
  file: File | null
  onCancel: () => void
  onDone: (annotated: File) => void
  /** When true, the "Skip" path returns the original file unchanged
   *  via `onDone(file)` so the caller's upload code stays one path. */
  allowSkipUnchanged?: boolean
}

export function ImageAnnotator({ open, file, onCancel, onDone, allowSkipUnchanged = true }: Props) {
  const { formatDate } = useOrgSettings()
  const formatStamp = makeFormatStamp(formatDate)
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [color, setColor] = useState(DEFAULT_COLOR)
  const [drawing, setDrawing] = useState(false)
  const [exporting, setExporting] = useState(false)
  // When set, the captured-at moment is stamped in the bottom-right
  // of the saved image. Frozen when the user toggles it on so the
  // displayed time matches what gets baked in, even if the dialog
  // sits open for a while before save.
  const [timestamp, setTimestamp] = useState<Date | null>(null)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Load the picked file into an HTMLImageElement so we can read its
  // intrinsic dimensions + paint it as the canvas background.
  useEffect(() => {
    if (!file || !open) {
      setImage(null); setStrokes([]); setTimestamp(null); return
    }
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => setImage(img)
    img.onerror = () => onCancel()
    img.src = url
    return () => { URL.revokeObjectURL(url) }
  }, [file, open])

  // Redraw the canvas whenever strokes / image / size change. We
  // always render at image-native pixel dimensions, then CSS-scale
  // the canvas down to fit — DPR-aware sharpening is unnecessary
  // because we're already at source resolution.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image) return
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(image, 0, 0)
    for (const s of strokes) {
      ctx.strokeStyle = s.color
      ctx.lineWidth = s.width
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.beginPath()
      s.points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y)
        else ctx.lineTo(p.x, p.y)
      })
      ctx.stroke()
    }
    if (timestamp) drawTimestamp(ctx, canvas.width, canvas.height, timestamp, formatStamp)
  }, [image, strokes, timestamp])

  // Map a pointer event onto image-native coords by inverting the
  // CSS scale. `getBoundingClientRect` already handles any DPR
  // scaling the browser may be doing.
  function clientToImage(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } | null {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const sx = canvas.width  / rect.width
    const sy = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * sx,
      y: (e.clientY - rect.top)  * sy,
    }
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!image) return
    const p = clientToImage(e); if (!p) return
    ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
    setDrawing(true)
    setStrokes((prev) => [...prev, { color, width: STROKE_WIDTH, points: [p] }])
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing) return
    const p = clientToImage(e); if (!p) return
    setStrokes((prev) => {
      if (prev.length === 0) return prev
      const last = prev[prev.length - 1]!
      const next = { ...last, points: [...last.points, p] }
      return [...prev.slice(0, -1), next]
    })
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    setDrawing(false)
    try { (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId) } catch {}
  }

  function undo() { setStrokes((prev) => prev.slice(0, -1)) }
  function clearAll() { setStrokes([]) }

  /// Compose strokes onto a fresh canvas at image-native resolution,
  /// encode to PNG, and return a `File` with the source's filename.
  async function save() {
    if (!file || !image) return
    // If the user drew nothing AND no timestamp is set AND we're
    // allowed to skip, hand back the original file — avoids a
    // needless PNG re-encode that strips EXIF, balloons size, etc.
    if (strokes.length === 0 && !timestamp && allowSkipUnchanged) {
      onDone(file)
      return
    }
    setExporting(true)
    try {
      const out = document.createElement('canvas')
      out.width = image.naturalWidth
      out.height = image.naturalHeight
      const ctx = out.getContext('2d')
      if (!ctx) throw new Error('canvas 2d context unavailable')
      ctx.drawImage(image, 0, 0)
      for (const s of strokes) {
        ctx.strokeStyle = s.color
        ctx.lineWidth = s.width
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.beginPath()
        s.points.forEach((p, i) => {
          if (i === 0) ctx.moveTo(p.x, p.y)
          else ctx.lineTo(p.x, p.y)
        })
        ctx.stroke()
      }
      if (timestamp) drawTimestamp(ctx, out.width, out.height, timestamp, formatStamp)
      const blob: Blob | null = await new Promise((resolve) => out.toBlob(resolve, 'image/png'))
      if (!blob) throw new Error('toBlob returned null')
      // Keep the original name; just swap the extension to .png so the
      // server sees the right content-type after re-encoding.
      const dotIdx = file.name.lastIndexOf('.')
      const base = dotIdx > 0 ? file.name.slice(0, dotIdx) : file.name
      const out_name = `${base}.png`
      onDone(new File([blob], out_name, { type: 'image/png' }))
    } finally {
      setExporting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        Mark up image (optional)
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onCancel} aria-label="Close">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box ref={containerRef} sx={{ position: 'relative', textAlign: 'center', minHeight: 280 }}>
          {!image ? (
            <Box sx={{ py: 6 }}><CircularProgress size={28} /></Box>
          ) : (
            <canvas
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              style={{
                maxWidth: '100%',
                maxHeight: '60vh',
                borderRadius: 8,
                touchAction: 'none',   // stop the browser from scrolling while drawing
                cursor: 'crosshair',
                background: '#000',
              }}
            />
          )}
        </Box>

        {/* Toolbar — color swatches + undo / clear */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>
            COLOR
          </Typography>
          {COLORS.map((c) => (
            <Tooltip key={c} title={c} arrow>
              <IconButton
                size="small"
                onClick={() => setColor(c)}
                sx={{
                  width: 26, height: 26,
                  bgcolor: c,
                  border: '2px solid',
                  borderColor: color === c ? 'primary.main' : 'divider',
                  '&:hover': { bgcolor: c, opacity: 0.85 },
                }}
                aria-label={`Color ${c}`}
              >
                <span />
              </IconButton>
            </Tooltip>
          ))}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Undo last stroke" arrow>
            <span>
              <IconButton size="small" onClick={undo} disabled={strokes.length === 0}>
                <UndoOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear all" arrow>
            <span>
              <IconButton size="small" onClick={clearAll} disabled={strokes.length === 0}>
                <RestartAltOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          {/* Date/time stamp — toggles a bottom-right overlay so the
              saved image carries proof of when the photo was reviewed.
              Stamped at toggle-on so the timestamp is stable from that
              moment forward, even if the user lingers before saving. */}
          <Tooltip title={timestamp ? 'Remove timestamp' : 'Add date & time to bottom-right'} arrow>
            <Button
              size="small"
              variant={timestamp ? 'contained' : 'outlined'}
              color={timestamp ? 'primary' : 'inherit'}
              startIcon={<AccessTimeRoundedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setTimestamp((cur) => cur ? null : new Date())}
              sx={{
                fontSize: 11.5, fontWeight: 600, ml: 0.5, px: 1.25, height: 28,
                textTransform: 'none',
                borderColor: timestamp ? undefined : 'divider',
                color: timestamp ? undefined : 'text.secondary',
              }}>
              {timestamp ? formatStamp(timestamp) : 'Stamp time'}
            </Button>
          </Tooltip>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} sx={{ fontSize: 13, color: 'text.secondary' }} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!image || exporting}
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          {exporting
            ? 'Saving…'
            : strokes.length === 0 && !timestamp && allowSkipUnchanged
              ? 'Use original'
              : `Attach${strokes.length > 0 ? ` with ${strokes.length} mark${strokes.length === 1 ? '' : 's'}` : ''}${timestamp ? ' + timestamp' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/// Render the moment of capture in the bottom-right corner of the
/// canvas. White text with a soft dark shadow keeps it readable on
/// any background; font size scales with image height so the stamp
/// occupies roughly the same visual share regardless of resolution.
function drawTimestamp(ctx: CanvasRenderingContext2D, w: number, h: number, when: Date, formatStampFn: (d: Date) => string) {
  const fontSize = Math.max(16, Math.floor(h / 32))
  const pad = Math.max(12, Math.floor(h / 80))
  const text = formatStampFn(when)

  ctx.save()
  ctx.font = `600 ${fontSize}px "Inter", "Helvetica Neue", Arial, sans-serif`
  ctx.textBaseline = 'bottom'
  ctx.textAlign = 'right'
  ctx.shadowColor = 'rgba(0,0,0,0.75)'
  ctx.shadowBlur = Math.max(4, fontSize / 4)
  ctx.shadowOffsetY = 1
  ctx.fillStyle = '#FFFFFF'
  ctx.fillText(text, w - pad, h - pad)
  ctx.restore()
}

/// Human-readable local timestamp used both in the on-canvas stamp
/// and the toolbar's button label.
function makeFormatStamp(formatDate: (d: Date | string, includeTime?: boolean) => string) {
  return (d: Date) => formatDate(d, true)
}
